import { clone, uniqueId, values } from "lodash";
import * as React from "react";

import { CombinedNamespacePaths, FilterItemType, SegmentSeparated } from "@app/resource-filters/common";
import { Filter, FilterItem } from "@app/resource-filters/filter-items";
import { SavedFilters } from "@app/resource-filters/SavedFilters";
import { ResourceType } from "@app/resources";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FilterSet } from "@app/resource-filters/filter-sets/filterSet";
import { ResourceFilterSet } from "@app/resource-filters/filter-sets/resourceFilterSet";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";

interface CreateFilterProps {
  activeFilter: Filter;
  activeFilterSet: ResourceFilterSet;
  canEdit: boolean;
  hasFilters: boolean;
  resourceType: ResourceType;

  /** As the filter set controls are embedded in the creation component, this is called when a user loads a filter set. */
  onFilterSetChange: (filterSet: FilterSet) => void;
  /** Called when a branch-level filter-item is selected or deselected...if deselected is false the filterItem will be undefined */
  onSelectionChange: (selected: boolean, filterItem?: FilterItem) => void;
  /** Called before the filter form opens to edit filters */
  onWillEdit: () => void;
  /** Called befored the filter form closes after cancelling the edit */
  onWillCancelEdit: () => void;
  /** Called when edit is complete and save is requested. */
  onSaveFilter: (filterItems: FilterItem[]) => Promise<void>;
}

interface CreateFilterState {
  canSaveChanges: boolean;
  editing: boolean;
  /** Maintains the order of the items and used to iterate over...the point is for this array of key string to ALWAYS have the same count and values as the keys in the lookup */
  leafKeys: string[];
  /** Maintains the mapping of key to item...to aid in fast lookup */
  leafLookup: LeafLookup;
  searchText: string;
  viewingSaved: boolean;
}

interface LeafLookup { [key: string]: SelectableLeaf; }

interface SelectableLeaf extends FilterItem { selected?: boolean; }

const VALID_FILTER_PATHS_SET = [
  "§ServiceAreas§Counties",
  "§ServiceAreas§States",
  "§Specialties",
  "§Tags"
];

class CreateFilter extends React.Component<CreateFilterProps, CreateFilterState> {
  // Used to track the selected keys seperately...when we're ready to 'save' the results.
  // It'll allow a fast lookup of only these keys and since we only use when 'saving' the results.
  // No need to put in state and trigger uneccessary re-renders
  private selectedLeafKeys: string[] = [];
  private menuRef = React.createRef<HTMLLIElement>();
  constructor(props: CreateFilterProps) {
    super(props);
    this.state = {
      canSaveChanges: false,
      editing: false,
      leafKeys: [],
      leafLookup: {},
      searchText: "",
      viewingSaved: false,
    };
  }

  public async componentDidUpdate(prevProps: CreateFilterProps, prevState: CreateFilterState) {
    if (prevProps.activeFilter !== this.props.activeFilter) {
      // Clear any old selections and create a new map that'll go to the state
      this.selectedLeafKeys = [];
      const leafKeys: string[] = [];
      const leafLookup: LeafLookup = {};

      //Populate map and generate keys...as I'm not sure display name can be relied on
      this.props.activeFilter.leaves.forEach((lf) => {
        const key = uniqueId("lf-");
        leafKeys.push(key);
        leafLookup[key] = (lf as SelectableLeaf);
      });

      this.setState((state) => ({
        ...state,
        leafKeys,
        leafLookup,
      }));
    }

    if (!prevState.editing && this.state.editing) {
      document.body.addEventListener("mousedown", this.handleDocumentBodyMouseDown);
    } else if (prevState.editing && !this.state.editing) {
      document.body.removeEventListener("mousedown", this.handleDocumentBodyMouseDown);
    }
  }

  public componentWillUnmount() {
    document.body.removeEventListener("mousedown", this.handleDocumentBodyMouseDown);
  }

  private setSearchText = (newSearchText: string): void => {
    this.setState((state) => ({
      ...state,
      searchText: newSearchText,
    }));
  }

  private shouldHideSearchInput = (): boolean => {
    const { filterPath } = this.props.activeFilter;
    return !VALID_FILTER_PATHS_SET.some(path => filterPath.includes(path));
  }

  public render() {
    const selectedNamespacePaths = values(this.state.leafLookup)
      .filter((v) => v.selected)
      .map((v) => v.namespacePath);
    const hasDataHistory = this.haveFilterHistory || this.state.viewingSaved;
    const hideSearch = this.shouldHideSearchInput();

    return (
      <>
        <li className="create-filter" data-editing={this.state.editing} ref={this.menuRef}>
          <div className="create-filter-title-area" data-history={hasDataHistory} onClick={this.handleBackClick}>

            {this.state.editing && (
              <>
                {hasDataHistory && (
                  <a href="#" className="pull-left pl4" onClick={preventDefaultMouseEvent}>
                    <QesIcon
                      iconName={QesIcons.ArrowNarrowLeft}
                      testId="create-filter__back-arrow"
                      className="left-icon align-text-top"
                      description="Return to previous menu"
                    />
                  </a>
                )}
                <span className="pr10">
                  {selectedNamespacePaths.length > 0 ? (
                    <CombinedNamespacePaths
                      disableRoot={true}
                      namespacePaths={selectedNamespacePaths}
                    />
                  ) : (
                    <SegmentSeparated text={this.props.activeFilter.filterPath.substring(1)} />
                  )}
                </span>
              </>
            )}

            <QESButton
              qesType="secondary"
              className="p-0 pull-right"
              qesTestId="create-filter__right-icon"
              onClick={(e) => { e.stopPropagation(); this.handleEditOrCancelClick(); }}
            >
              <QesIcon
                iconName={QesIcons.PlusCircle}
                description={this.state.editing ? "Close menu" : "Add filter"}
                className="right-icon color-caribbean-green align-text-top"
                width="16px"
                height="16px"
                testId="create-filter__right-icon"
              />
            </QESButton>
          </div>

          {this.state.editing && <div className="create-filter-list" >
            {!hideSearch ?
              <QESTextInput
                testId={`create-filter__search-input`}
                hideLabel={true}
                label=""
                placeholder="Search..."
                value={this.state.searchText}
                onChange={(e) => this.setSearchText(e.target.value)}
                containerClassName="checkbox-dropdown__label"
              /> : null
            }
            <ul>
              {this.state.viewingSaved ? (
                <SavedFilters
                  activeFilterSet={this.props.activeFilterSet}
                  resourceType={this.props.resourceType}
                  onFilterSetChange={this.props.onFilterSetChange}
                />
              ) : (
                <>
                  {this.props.activeFilter.branches.map((fi) => {
                    if (this.state.searchText && !fi.displayName.toLowerCase().includes(this.state.searchText.toLowerCase())) {
                      return null;
                    }

                    return (
                      <li key={fi.displayName} className="filter-item branch-item clearfix">
                        <QESButton
                          className="branch-item-button"
                          qesType="none"
                          qesTestId={`create-filter__branch--${fi.displayName}`}
                          onClick={() => this.handleBranchItemClick(fi)}
                        >
                          {fi.displayName}
                          <QesIcon iconName={QesIcons.ChevronRight} description="Next" className="align-text-top" />
                        </QESButton>
                      </li>
                    );
                  })}

                  {this.state.leafKeys.map((key) => {
                    const fi = this.state.leafLookup[key];

                    if (this.state.searchText && !fi.displayName.toLowerCase().includes(this.state.searchText.toLowerCase())) {
                      return null;
                    }

                    return (
                      <li key={key} className="filter-item leaf-item clearfix">
                        <div
                          className="d-flex flex-row align-items-center"
                          onClick={(e) => {
                            (e.target as Element).querySelector<HTMLInputElement>("input[type='checkbox']")?.focus();
                            e.stopPropagation();
                            this.handleLeafItemClick(key);
                          }}
                        >
                          <span className="pe-1">
                            <QESCheckbox
                              label={fi.displayName}
                              checked={!!fi.selected}
                              onChange={() => { this.handleLeafItemClick(key); }}
                              // prevent the click handler for the checkbox and the div both firing
                              onClick={(e) => { e.stopPropagation(); }}
                              onLabelClick={(e) => { e.stopPropagation(); }}
                              testId={`create-filter__checkbox--${key}`}
                            />
                          </span>

                          <div className="resource-count-badge ms-auto">
                            {fi.resourceCount === undefined ? "-" : fi.resourceCount}
                          </div>
                        </div>
                      </li>
                    );
                  })}

                  <li className="filter-item separator-item clearfix" />

                  {this.state.leafKeys.length === 0 && (
                    <li className="filter-item branch-item clearfix" >
                      <QESButton
                        className="branch-item-button"
                        qesType="none"
                        qesTestId="create-filter__branch--saved-filters"
                        onClick={this.handleSavedFiltersClick}
                      >
                        Saved Filters
                        <QesIcon iconName={QesIcons.ChevronRight} description="Next" className="align-text-top" />
                      </QESButton>
                    </li>
                  )}

                  {((this.state.leafKeys.length === 0 && this.props.activeFilter.branches.length === 0) || null) && (
                    <li className="filter-item empty-item">
                      No items found, given the current filters.
                    </li>
                  )}
                </>
              )}
            </ul>
            {((this.state.leafKeys.length > 0) || null) && (
              <div className="leaf-filter-save" >
                <QESButton qesTestId="create-filter_set-filter" disabled={!this.state.canSaveChanges} onClick={this.handleSaveChanges}>
                  Set Filter
                </QESButton>
              </div>
            )}
          </div>}

        </li>
        {((!this.props.hasFilters && !this.state.editing) || null) && (
          <li className="add-a-filter-label" onClick={this.handleEditOrCancelClick}>
            Add a Filter
          </li>
        )}
      </>
    );
  }

  private handleSavedFiltersClick = () => {
    this.setState({ viewingSaved: true });
  }

  /* Due to the nature of how this form is setup, going 'back' is essentially deselecting */
  private handleBackClick = () => {
    if (this.haveFilterHistory) {
      this.props.onSelectionChange(false);
    }

    this.setState((prevState) => ({
      ...prevState,
      searchText: "",
      viewingSaved: false,
    }));
  }

  private handleBranchItemClick = (filterItem: FilterItem) => {
    if (this.props.canEdit) {
      if (filterItem.itemType === FilterItemType.Branch) {
        this.props.onSelectionChange(true, filterItem);

        this.setState((prevState) => ({
          ...prevState,
          searchText: "",
        }));
      }
    }
  }

  private handleDocumentBodyMouseDown = (e: MouseEvent) => {
    const clickTarget = e.target;
    if (isInsideContainer(clickTarget, this.menuRef.current)) {
      return;
    }

    if (this.state.editing) {
      this.handleEditOrCancelClick();
    }
  }

  private handleEditOrCancelClick = () => {
    if (this.props.canEdit) {
      if (this.state.editing) {
        this.props.onWillCancelEdit();
      }

      if (!this.state.editing) {
        this.props.onWillEdit();
      }

      this.setState((prevState) => ({
        ...prevState,
        editing: !prevState.editing,
        viewingSaved: false,
      }));
    }
  }

  private handleLeafItemClick = (key: string) => {
    // Get the UI related items done and set in state first
    const leaf = clone(this.state.leafLookup[key]);
    const selected = !leaf.selected;

    leaf.selected = selected;
    const leafLookup = { ...this.state.leafLookup, [key]: leaf };

    this.setState((prevState) => ({
      ...prevState,
      leafLookup,
    }));

    // Then take care of our tracking array
    if (selected) {
      this.selectedLeafKeys.push(key);
    } else {
      const index = this.selectedLeafKeys.indexOf(key);
      if (index > -1) {
        this.selectedLeafKeys.splice(index, 1);
      }
    }

    // Lastly enable/disable stuff
    const canSaveChanges = this.selectedLeafKeys.length > 0;
    if (this.state.canSaveChanges !== canSaveChanges) {
      this.setState((prevState) => ({
        ...prevState,
        canSaveChanges,
      }));
    }
  }

  private handleSaveChanges = () => {
    this.setState({ canSaveChanges: false }, async () => {
      const leaves: FilterItem[] = [];

      this.selectedLeafKeys.forEach((key) => {
        leaves.push(this.state.leafLookup[key]);
      });

      await this.props.onSaveFilter(leaves);

      this.setState({ editing: false });
    });
  }

  /* Not the best, but it works for now */
  private get haveFilterHistory(): boolean { return this.props.activeFilter.filterPath.length > 0; }
}

function isInsideContainer(target: EventTarget, container: HTMLElement) {
  while (target !== document.body) {
    if (target === container) {
      return true;
    }
    target = (target as HTMLElement).parentNode;
  }
  return false;
}

export { CreateFilter };
